import { FC, useState, useEffect } from "react";
import { PlayCircle, BorderColorOutlined } from "@mui/icons-material";
import { Autocomplete, Box, Button, Chip, Switch, TextField, Tooltip, Typography } from "@mui/material";
import { axiosInstance } from "../../services/axios";
import { filter, lowerCase, isEmpty, upperCase } from "lodash";
import Cron from "react-js-cron-mui";
import { IAlertState, useAlertState } from "../../store/alertState";
import usePermissionChecker from "../../hooks/process/usePermissionChecker";
import { API_SLUGS, SCAN_TOOLS } from "../../constants";
import { PermissionTargets, PermissionTypes } from "../../store/userState";
import ModalWrapper from "../common/modal/wrapper";
import MaterialReactTable from "material-react-table";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Plus } from "../../icons/plus";
import { cronToReadable, displayDateTime } from "../../utils/conversions";
import Loaders from "./Loaders";
import CronBuilder from "../common/cronBuilder";

interface FormEditorProps {
    callback?: () => void;
  }

const CloudRunnerConfig: FC<any> = ({ assetId = undefined, assetName = undefined, projectId, presetRepo = undefined, environmentId = undefined, trigger = undefined, sx = undefined, type = undefined }) => {

    const [scanTool, setScanTool] = useState({type: "sast", tool: "semgrep", group: "SAST"});
    const [cronValue, setCronValue] = useState("30 10 * * *");
    const [jobs, setJobs] = useState([] as any);
    const [assets, setAssets] = useState<any>({});
    const [selectedBranch, setSelectedBranch] = useState<any>({});
    const [selectedAsset, setSelectedAsset] = useState<any>({id: assetId, name: assetName});
    const [selectedEnvironment, setSelectedEnvironment] = useState<any>({});
    const [repository, setRepository] = useState([] as any);
    const [expandedJob, setExpandedJob] = useState("new");
    const [environments, setEnvironments] = useState([] as any);
    const [branchName, setBranchName] = useState("")
    const [editJobValues, setEditJobValues] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [jobsLoading, setJobsLoading] = useState<boolean>(false);
    
    const { hasPermission } = usePermissionChecker(
        PermissionTargets.configuration,
        PermissionTypes.execute,
    );
    const { setMessage, clearMessage } = useAlertState(
        (state) => state,
    ) as IAlertState;

    useEffect(() => {
        initializeData();
    }, []);

    const toolCommands = SCAN_TOOLS.filter((it: any) => it.cloudRunner);

    const getIntegrationType = (url: string) => ["gitlab", "github", "bitbucket"] // find just the first occurence, string might contain another vcs name
        .map((type: string) => ({
            name: type,
            idx: url.toLowerCase().indexOf(type)
        })).filter((it: any) => it.idx > -1).sort((a: any, b: any) => a.idx - b.idx)[0]?.name || "";

    const initializeData = async () => {
        const firstAsset = await getRepo();
        setSelectedAsset(firstAsset);
        await getJobs(firstAsset);
        if (firstAsset?.projectId) {
            await getEnvironments(firstAsset);
        }
    }

    const findRepo = (repositories: any, assetId: string) => {
        let repository = {};
        for (const repo of repositories) {
            if (repo?.details?.assets && repo?.details.assets.length > 0) {
                if (repo?.details.assets.some((it: any) => it.id === assetId)) {
                    repository = repo;
                }
            }
        }
        return repository;
    }

    const getJobs = async (asset: any) => {
        if (!asset?.id) {
            return;
        }
        setJobsLoading(true)
        try {
            let apiResponse = await axiosInstance.get(`/jobs/scanners/${asset?.id}`);
            setJobs(apiResponse.data);
        } catch (error: any) { }
        setJobsLoading(false)
    };

    const getRepo = async () => {
        let firstAsset = { id: "", name: "", projectId: "" };
        if (!presetRepo) {
            setLoading(true);
            try {
                let apiResponse = await axiosInstance.get(API_SLUGS.GITLAB_PROJECTS);
                let searchResult = findRepo(apiResponse.data?.projects, assetId);
                if (isEmpty(searchResult)) {
                    apiResponse = await axiosInstance.get(API_SLUGS.GITHUB_PROJECTS);
                };
                searchResult = findRepo(apiResponse.data?.projects, assetId);
                setRepository(searchResult);
                firstAsset = { id: assetId, name: assetName || assetId, projectId }
                setAssets([firstAsset]);
            } catch (error: any) { }
            setLoading(false);
        } else {
            setRepository(presetRepo);
            const presetAssets = presetRepo?.details?.assets
            setAssets(presetAssets || []);
            if (presetAssets && presetAssets.length > 0) {
                firstAsset = presetAssets[0];
            }
        }
        return firstAsset;
    };

    const getEnvironments = async (asset: any) => {
        try {
            const path = API_SLUGS.ADD_ENVIRONMENT.replace('{projectId}', asset?.projectId)
            const response = await axiosInstance.get(path);
            setEnvironments(response?.data?.environments)
        } catch (error: any) { }
    }

    const createJob = async (jobValues: any = undefined) => {
        const toolInfo = toolCommands.find((it: any) => it.type === scanTool.type && it.tool === scanTool.tool);
        try {
            await axiosInstance.post(`/jobs/scanners`, {
                cron: cronValue,
                assetId: jobValues?.assetId || selectedAsset?.id,
                environmentId: jobValues?.environmentId || selectedEnvironment?.id,
                branch: jobValues?.branch || selectedBranch?.name,
                toolType: jobValues?.scanType || toolInfo?.type,
                toolName: jobValues?.toolName || toolInfo?.tool,
                repository: getIntegrationType(repository?.url)
            }
            );
        } catch (error: any) { }
    };

    const updateJob = async () => {
        const toolInfo = toolCommands.find((it: any) => it.type === scanTool.type && it.tool === scanTool.tool);
        try {
            await axiosInstance.patch(`/jobs/scanners/${expandedJob}`, {
                cron: cronValue,
                assetId: selectedAsset?.id,
                environmentId: selectedEnvironment?.id,
                branch: selectedBranch?.name,
                toolType: toolInfo?.type,
                toolName: toolInfo?.tool
            }
            );
        } catch (error: any) { }
    };

    const toggleJobStatus = async (jobId: string) => {
        const action = jobs.find((it: any) => it.uuid === jobId)?.enabled ? "Disable" : "Enable";
        try {
            await axiosInstance.patch(`/jobs/scanners/${jobId}`, { enabled: action === "Enable" ? "true" : "false" });
            await getJobs(selectedAsset);
        } catch (error: any) { }
    };

    const setFormValues = (job: any) => {
        const toolInfo = toolCommands.find((it: any) => it.type === job?.meta?.toolType && it.tool === job?.meta?.toolName);
        setCronValue(job.cron || "30 10 * * *");
        setScanTool( (toolInfo?.type && toolInfo?.tool) ? { type: toolInfo.type, tool: toolInfo.tool, group: toolInfo.group} : {type: "sast", tool: "semgrep", group: "SAST"} );
        setSelectedAsset({ id: job.assetId, name: job?.assetName || job.assetId });
        //setSelectedBranch({ name: job.branch || repository?.details?.branches[0]?.name || "" });
        setBranchName(job.branch);
        setSelectedEnvironment( environments.find((it: any) => it?.id === job?.environmentId) );
        setSelectedBranch({ name: job.branch });
    }

    const toggleExpandedJob = (job: any) => {
        if (expandedJob === job.uuid) {
            setExpandedJob(prev => (prev !== "new" ? "new" : ""));
            setFormValues({ id: job.assetId, name: job.assetName });
        } else {
            setExpandedJob(job.uuid);
            setFormValues(job);
        }
    };

    const saveJobForm = async (callback: any) => {
        clearMessage();
        if (expandedJob === 'new') {
            await createJob();
            setMessage({
                title: "Created scheduled job",
                type: "success",
            });
        } else {
            await updateJob();
            setMessage({
                title: "Updating scheduled job",
                type: "success",
            });

        }
        await getJobs(selectedAsset);
       
        if (callback) {
            callback()
        }
    }

    const deleteJob = async (jobId: any) => {
        try {
            await axiosInstance.delete(`/jobs/scanners/${jobId}`);
            await getJobs(selectedAsset);
            setMessage({title: "Job Deleted", type: 'success'})
        } catch (error: any) { }
    };

    const runJob = async (jobId: any, callback: any = undefined) => {
        try {
                await axiosInstance.post(`/jobs/scanners/${jobId}`);
                await getJobs(selectedAsset);

            }
            catch (error: any) { }
    };

    const FormEditor: FC<FormEditorProps> = ({ callback }) => {
        return (
            <Box>
                <Typography
                color="textPrimary"
                sx={{ pt: 2, mb: 1, textAlign: "left" }}
                variant="h6"
            >
                Scan type
            </Typography>
            <Autocomplete
                disableClearable
                clearOnBlur
                options={toolCommands}
                getOptionLabel={(option: any) => `${option.group} / ${option.tool}`}
                groupBy={(option) => option.group || "Other"}
                value={scanTool}
                onChange={(e, value) => { setScanTool(value); }}
                sx={{ maxWidth: "90%", textAlign: 'left', mt: 2 }}
                size="medium"
                renderInput={(params): JSX.Element => {
                    return <TextField
                        name="toolCommand"
                        variant="outlined"
                        helperText={"Type of scan to run"}
                        {...params}
                    />
                }}
            />
                
                <Box display={'flex'} sx={{alignItems: 'center'}} width='95%'>
                    <Box width='50%'>
                <Typography
                    color="textPrimary"
                    sx={{ pt: 2, mb: 1, textAlign: "left" }}
                    variant="h6"
                >
                    Asset
                </Typography>
                <Autocomplete
                    disableClearable
                    clearOnBlur
                    options={assets.length > 0 ? assets : []}
                    value={selectedAsset}
                    disabled={!(assets && assets.length > 0)}
                    getOptionLabel={(option: any) => option?.name ?? ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(e, value) => {
                        setSelectedAsset(value);
                        getEnvironments(value)
                    }
                    }
                    filterOptions={(options, params) => {
                        return filter(options, item =>
                            lowerCase(item.name).includes(lowerCase(params.inputValue))
                        );
                    }}
                    sx={{ maxWidth: "90%", textAlign: 'left', mt: 1 }}
                    size="medium"
                    renderInput={(params): JSX.Element => {
                        return <TextField
                            name="assetId"
                            variant="outlined"
                            helperText={"Asset to associate run result with"}
                            {...params}
                        />
                    }}
                />
                </Box>
                <Box width='50%'>
                <Typography
                    color="textPrimary"
                    sx={{ pt: 2, mb: 1, textAlign: "left" }}
                    variant="h6"
                >
                    Environment
                </Typography>
                <Autocomplete
                    disableClearable
                    clearOnBlur
                    options={environments}
                    value={selectedEnvironment}
                    getOptionLabel={(option: any) => option?.name ?? ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(e, value) => setSelectedEnvironment(value)}
                    filterOptions={(options, params) => {
                        return filter(options, item =>
                            lowerCase(item.name).includes(lowerCase(params.inputValue))
                        );
                    }}
                    sx={{ maxWidth: "90%", textAlign: 'left', mt: 1 }}
                    size="medium"
                    renderInput={(params): JSX.Element => {
                        return <TextField
                            name="environmentId"
                            variant="outlined"
                            helperText={"Environment to associate run result with"}
                            {...params}
                        />
                    }}
                />
                </Box>
                </Box>
                <Box display={'flex'} width='95%' sx={{alignItems: 'center'}}>
                    <Box width='50%'>
                <Typography
                    color="textPrimary"
                    sx={{ pt: 2, mb: 1, textAlign: "left" }}
                    variant="h6"
                >
                    Repository URL
                </Typography>
                <TextField
                    sx={{ maxWidth: "90%", textAlign: 'left' }}
                    size="medium"
                    fullWidth
                    name="RepositoryURL"
                    helperText={"Repository in connected VCS"}
                    value={repository?.url}
                    disabled={true}
                    variant="outlined"
                />
                </Box>
                <Box width='50%'>
                <Typography
                    color="textPrimary"
                    sx={{ pt: 2, mb: 1, textAlign: "left" }}
                    variant="h6"
                >
                    Branch
                </Typography>
                <Autocomplete
                    disableClearable
                    clearOnBlur
                    options={repository?.details?.branches}
                    getOptionLabel={(option: any) => option?.name ?? ''}
                    sx={{ maxWidth: "90%", textAlign: 'left', mt: 1 }}
                    size="medium"
                    onChange={(e, value) => setSelectedBranch(value)}
                    filterOptions={(options, params) => {
                        return filter(options, item =>
                            lowerCase(item.name).includes(lowerCase(params.inputValue))
                        );
                    }}
                    value={selectedBranch}
                    renderInput={(params): JSX.Element => {
                        return <TextField
                            name="branchName"
                            variant="outlined"
                            helperText={"Branch to run the scan on"}
                            {...params}
                        />
                    }}
                />
                </Box>
                </Box>
                <Typography
                    color="textPrimary"
                    sx={{ pt: 2, textAlign: "left" }}
                    variant="h6"
                >
                    Run schedule
                </Typography>  
                <CronBuilder
                    value={cronValue}
                    onChange={(newCron: string) => {
                        setCronValue(newCron);
                    }}
                    />
                

                    <Button
                        variant={"contained"}
                        onClick={() => {saveJobForm(callback)}}
                        size="medium"
                        disabled={!selectedAsset?.id || !selectedBranch?.name || !selectedEnvironment?.id}
                        sx={{mt:2}}
                    >
                        Save
                    </Button>


            </Box>
        )
    };

    const jobsTable = (jobs: any, toolCommands: any) => {
        return (
            <>
                <Box>
                    <ModalWrapper
                        maxWidth="md"

                        title={`Add Run`}
                        trigger={
                            <Button variant='outlined' size="small"
                                onClick={() => {
                                    setFormValues({});
                                    setExpandedJob("new")
                                }}
                                sx={{ borderColor: 'neutral.300', color: 'text.primary' }}>
                                Add <Plus />
                            </Button>

                        }
                        child={<FormEditor />}

                    />
                </Box>
                <MaterialReactTable
                    muiTablePaperProps={{
                        elevation: 0,
                        sx: { marginBottom: 0 }
                    }}
                    enableGlobalFilter={false}
                    columns={[
                        {
                            accessorKey: "tool",
                            header: "Tool",
                            Cell: (params: any) => {
                                return (
                                    <Typography>{params?.row?.original?.meta?.toolName}</Typography>
                                )
                            },
                            size: 130,
                        },
                        {
                            accessorKey: "type",
                            header: "Type",
                            Cell: (params: any) => (
                                <Chip label={upperCase(params?.row?.original?.meta?.toolType)} size='small' />
                            ),
                            size:130
                        },
                        {
                            accessorKey: "repository",
                            header: "Repository",
                            Cell: (params: any) => {
                                return (<Typography>{repository?.name}</Typography>)
                            }

                        },
                        {
                            accessorKey: "branch",
                            header: "Branch",
                            Cell: (params: any) => {
                                return (<Typography>{params?.row?.original?.branch}</Typography>)
                            }

                        },
                        {
                            accessorKey: "cron",
                            header: "Schedule",
                            Cell: (params: any) => (
                                <Typography>{cronToReadable(params?.row?.original?.cron)}</Typography>
                            )

                        },
                        {
                            accessorKey: "lastRun",
                            header: "Last Run",
                            Cell: (params: any) => (
                                <Typography>{displayDateTime(params?.row?.original?.lastRun)}</Typography>
                            )

                        },
                        {
                            accessorKey: "enabled",
                            header: "Enable",
                            Cell: (params: any) => (

                                <Switch
                                    checked={params?.row?.original?.enabled}

                                    onChange={() => {
                                        toggleJobStatus(params?.row?.original?.uuid)
                                    }}
                                />

                            ),
                            size:80

                        },
                        {
                            accessorKey: "runNow",
                            header: "Run Now",
                            Cell: (params: any) => (
                                <Button
                                    size="small"

                                    onClick={() => {
                                        runJob(params?.row?.original?.uuid)
                                    }}
                                //sx={{borderColor: 'neutral.300', color: 'text.primary' }}
                                >
                                    <PlayCircle />

                                </Button>
                            ),
                            size: 60

                        },
                        {
                            //@ts-ignore
                            accessorKey: "actions",
                            header: "Actions",
                            Cell: (params: any) => {
                                return (

                                    <Box sx={{ display: "flex", justifyContent: "start", alignContent: "top" }}>
                                        <ModalWrapper
                                            maxWidth="md"

                                            title={`Edit Run`}
                                            trigger={

                                                <Button variant='outlined' size='small' sx={{ color: 'text.primary', borderColor: 'neutral.300', mr: 1, minWidth: '45px' }} onClick={() => {

                                                    setFormValues(params?.row?.original);
                                                    setExpandedJob(params?.row?.original?.uuid)
                                                }}>
                                                    <BorderColorOutlined />
                                                </Button>

                                            }
                                            child={
                                                <>
                                                    <FormEditor callback={() => { }} />
                                                </>

                                            }

                                        />

                                        <Tooltip title="Delete">
                                            <Button color='error' onClick={() => deleteJob(params?.row?.original?.uuid)} variant='outlined' size='small' sx={{ borderColor: 'neutral.300', mr: 1, minWidth: '45px' }}>
                                                <DeleteOutlineIcon />
                                            </Button>
                                        </Tooltip>

                                    </Box>

                                )
                            },
                        },
                    ]}
                    data={jobs}
                    initialState={{
                        density: "compact",
                        columnPinning: { left: ['tool'] },
                    }}

                    enableColumnActions={false}
                    enableSorting={false}
                    enableStickyHeader
                    muiTableBodyCellProps={{
                        align: 'justify',
                    }}

                    muiTableContainerProps={{
                        sx: { maxHeight: '60vh', overflow: 'auto' },
                    }}
                    enableTopToolbar={false}
                    enablePagination={false}
                    enableDensityToggle={false}
                    enableColumnFilters={false}
                />
            </>
        )
    }
    if (trigger) {
        return (
            <Box sx={{ display: "inline", textAlign: "left", ...sx }}>
                <ModalWrapper
                    title={"Run scanners tool"}
                    maxWidth="lg"
                    trigger={trigger ? trigger : <Chip sx={{ mt: 2, border: 0 }}
                        color="primary"
                        variant="outlined" icon={<PlayCircle />} label="Analyze" />}
                    child={
                        <Box sx={{ minWidth: "50em" }}>
                            {jobsTable(jobs, toolCommands)}
                        </Box>}
                />
            </Box>
        )
    }

    if(loading || jobsLoading) {
        return(
            <Loaders />
        )
    }

    return (<Box sx={{ minWidth: "50em" }}>
        {!repository?.id ? (
            <Typography variant="h6">
                {"No VCS integration found."}
            </Typography>
        ) : (

            <Box sx={{ overflow: "visible" }}>
                {jobsTable(jobs, toolCommands)}
            </Box>
        )}
    </Box>)
}

export default CloudRunnerConfig;
